import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { num, shortDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ExpandToggle } from "@/components/ExpandToggle";
import { PairRow } from "@/components/PairRow";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

const TITLES = { 1: "cosa comprano nel primo ordine", 2: "cosa comprano nel secondo ordine", 3: "quali capi del primo ordine li fanno tornare" };

export const FirstOrderInsights = () => {
  const [group, setGroup] = useState("model");
  const [order, setOrder] = useState(1);
  const [all, setAll] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["first-order", group, order],
    queryFn: async () => (await api.get("/sales/new-customers-first-order", { params: { group, order } })).data });
  return (
    <Card className="mt-6 p-6" data-testid="first-order-insights">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Nuovi clienti {data?.season} · {TITLES[order]}</h3>
          {data?.since && order === 1 && <p className="text-xs text-muted-foreground" data-testid="first-order-summary">
            {num(data.new_customers)} nuovi clienti dal {shortDate(data.since)} · {num(data.with_season)} con capi {data.season} nel primo ordine</p>}
          {data?.since && order === 2 && <p className="text-xs text-muted-foreground" data-testid="second-order-summary">
            <span className="font-semibold text-foreground">{num(data.repeat)} su {num(data.new_customers)}</span> nuovi clienti hanno fatto un secondo ordine ({data.repeat_pct}%)
            {data.avg_days != null && <> · in media dopo {num(data.avg_days)} giorni</>} · {num(data.with_season)} con capi {data.season} nel secondo ordine</p>}
          {data?.since && order === 3 && <p className="text-xs text-muted-foreground" data-testid="pairs-summary">
            Tasso di ritorno per capo del primo ordine · solo capi comprati da almeno {data.min_customers} nuovi clienti · clicca per vedere cosa hanno preso nel secondo</p>}
          {data?.since && (
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              <Link to="/clienti?new_season=senza_secondo" className="text-primary hover:underline" data-testid="link-senza-secondo">
                → {num(data.new_customers - data.repeat)} senza secondo ordine, da ricontattare</Link>
              <Link to="/clienti?new_season=con_secondo" className="text-muted-foreground hover:text-foreground hover:underline" data-testid="link-con-secondo">
                → {num(data.repeat)} con secondo ordine</Link>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {[[1, "Primo ordine"], [2, "Secondo acquisto"], [3, "Dal primo al secondo"]].map(([k, l]) => (
            <button key={k} onClick={() => { setOrder(k); setAll(false); }} data-testid={`first-order-tab-${k}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${order === k ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-secondary/50"}`}>{l}</button>
          ))}
          <span className="mx-1 w-px bg-border" />
          {[["model", "Per modello"], ["category", "Per categoria"]].map(([k, l]) => (
            <button key={k} onClick={() => { setGroup(k); setAll(false); }} data-testid={`first-order-group-${k}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${group === k ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-secondary/50"}`}>{l}</button>
          ))}
        </div>
      </div>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <>
          <div className="space-y-3">
            {(all ? data.items : data.items.slice(0, 15)).map((i, idx) => order === 3 ? <PairRow key={i.name} r={i} idx={idx} /> : (
              <div key={i.name} data-testid={`first-order-item-${idx}`}>
                <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{i.name}</span>
                  <span className="shrink-0 font-mono-erp text-xs text-muted-foreground">{num(i.customers)} {i.customers === 1 ? "cliente" : "clienti"} · {num(i.units)} pz · {i.pct}%</span>
                </div>
                <Progress value={i.pct} className="h-2" />
              </div>
            ))}
            {!data.items.length && <p className="text-sm text-muted-foreground">{order === 1 ? "Nessun nuovo cliente in questa stagione." : "Nessun secondo ordine con capi della stagione."}</p>}
          </div>
          <ExpandToggle total={data.items.length} open={all} onToggle={() => setAll(!all)} testid="first-order-toggle" />
        </>
      )}
    </Card>
  );
};
